import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TAppDispatch, TRootState } from '#/redux/store';
import ApiManager from '#/services/api/ApiManager';
import { apiConfig } from '#/utils/constants';
import generateAuthParams from '#/utils/generateHash';
import Pagination from '#/components/layout/Pagination/Pagination';
import './Comics.style.scss';
import Input from '#/components/form/Input/Input';
import {
  calculateOffset,
  calculateTotalPages,
  debounce,
} from '#/utils/functions';
import { useNavigate } from 'react-router-dom';
import { RoutesNames } from '#/router/routes';
import CharactersLayout from '#/components/layout/CharactersLayout/CharactersLayout';
import ComicPreview from '#/components/layout/ComicPreview/ComicPreview';
import { ComicsActionType } from '#/redux/actions/comics';
import Select from '#/components/form/Select/Select';
import { IMarvelAPIResponse } from '#/types/common';
import IComic from '#/types/comics';
import { BookMarksActionType } from '#/redux/actions/bookmarks';
import ContextMenuLayer, {
  IContextMenuOption,
} from '#/components/layout/ContextMenuLayer/ContextMenuLayer';
import { HidenItemsActionType } from '#/redux/actions/hidenItems';
import Button from '#/components/form/Button/Button';
import NoDataFound from '#/components/layout/NoDataFound/NoDataFound';
import { useError } from '#/hooks/useError/useError';

const Comics = () => {
  const limit = 20;

  const loadings = useRef(0);

  const { addError } = useError();

  const comicsSelector = (state: TRootState) => {
    return state.comics;
  };

  const bookmarksSelector = (state: TRootState) => {
    const bookmarkComics = state.bookmarks.bookmarks.filter(
      (bookmark) => bookmark.type === 'comic'
    );
    return bookmarkComics;
  };

  const hidenItemsSelector = (state: TRootState) => {
    const hidenItemsComics = state.hidenItems.hidenItems.filter(
      (hidenItem) => hidenItem.type === 'comic'
    );
    return hidenItemsComics;
  };

  const comicsState = useSelector(comicsSelector);

  const hidenComics = useSelector(hidenItemsSelector);

  const bookmarkedComics = useSelector(bookmarksSelector);

  const { comics, search, selectedFormat, page, totalPages, loading } =
    comicsState;

  const dispatch = useDispatch<TAppDispatch>();

  const navigate = useNavigate();

  const getComics = useCallback(
    async (
      currentPage: number,
      currentSelectedFormat: string,
      currentSearch: string
    ) => {
      const apiManager = new ApiManager(apiConfig);

      loadings.current++;

      const newComics = await apiManager.getAllEntities<
        IMarvelAPIResponse<IComic>
      >('COMICS', {
        ...generateAuthParams(),
        limit: limit,
        offset: calculateOffset(currentPage, limit),
        titleStartsWith: currentSearch ? currentSearch : undefined,
        format: currentSelectedFormat ? currentSelectedFormat : undefined,
      });

      loadings.current--;

      if (loadings.current === 0) {
        dispatch({
          type: ComicsActionType.COMICS_STOP_LOADING,
        });
      }

      if ('error' in newComics) {
        addError(newComics.error);
      } else {
        if (newComics.data.data?.results) {
          dispatch({
            type: ComicsActionType.COMICS_SET_COMICS,
            payload: newComics.data.data.results,
          });
        }
        if (
          newComics.data.data?.total != undefined &&
          newComics.data.data.limit != undefined
        ) {
          dispatch({
            type: ComicsActionType.COMICS_SET_TOTAL_PAGES,
            payload: calculateTotalPages(
              newComics.data.data.total,
              newComics.data.data.limit
            ),
          });
        }
      }
    },
    [addError, dispatch]
  );

  const debouncedGetComics = useMemo(
    () => debounce(getComics, 750),
    [getComics]
  );

  useEffect(() => {
    dispatch({
      type: ComicsActionType.COMICS_START_LOADING,
    });
    debouncedGetComics(page, selectedFormat, search);
  }, [debouncedGetComics, dispatch, page, search, selectedFormat]);

  useEffect(() => {
    dispatch({ type: ComicsActionType.COMICS_SET_PAGE, payload: 1 });
  }, [dispatch, search, selectedFormat]);

  const handleSearchChange = (value: string) => {
    dispatch({
      type: ComicsActionType.COMICS_START_LOADING,
    });

    dispatch({
      type: ComicsActionType.COMICS_SET_SEARCH,
      payload: value,
    });
  };

  const handleFormatChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (typeof event.currentTarget.value === 'string') {
      dispatch({
        type: ComicsActionType.COMICS_START_LOADING,
      });

      dispatch({
        type: ComicsActionType.COMICS_SET_SELECTED_FORMAT,
        payload: event.currentTarget.value,
      });
    }
  };

  const handlePageClick = (newPage: number) => {
    dispatch({
      type: ComicsActionType.COMICS_START_LOADING,
    });

    dispatch({
      type: ComicsActionType.COMICS_SET_PAGE,
      payload: newPage,
    });
  };

  const handleShowHiden = () => {
    dispatch({
      type: HidenItemsActionType.HIDEN_ITEMS_REMOVE_ALL_HIDEN_ITEMS,
      payload: 'comic',
    });
  };

  return (
    <div>
      <div className="comics--filters">
        <div className="comics--filter">
          <label>Title:</label>
          <Input
            data-testid="comicSearch"
            value={search}
            handleOnChange={handleSearchChange}
          />
        </div>

        <div className="comics--filter">
          <label>Format:</label>

          <Select
            data-testid="comicFormat"
            value={selectedFormat}
            onChange={handleFormatChange}
            options={[
              {
                text: 'Any',
                value: '',
              },
              {
                text: 'Comic',
                value: 'comic',
              },
              {
                text: 'Magazine',
                value: 'magazine',
              },
              {
                text: 'Hardcover',
                value: 'hardcover',
              },
              {
                text: 'Trade paperback',
                value: 'trade paperback',
              },
              {
                text: 'Digest',
                value: 'digest',
              },
              {
                text: 'Graphic novel',
                value: 'graphic novel',
              },
              {
                text: 'Digital comic',
                value: 'digital comic',
              },
              {
                text: 'Infinite comic',
                value: 'infinite comic',
              },
            ]}
          />
        </div>
      </div>

      {hidenComics.length > 0 && (
        <div className="comics--filters">
          {comics.find(
            (comic) =>
              hidenComics.find((hidenComic) => hidenComic.id === comic.id) !==
              undefined
          ) !== undefined && (
            <div className="comics--filter">
              <span>There are hiden elements in this page.</span>
            </div>
          )}

          <div className="comics--filter">
            <Button
              text="Show hiden"
              handleOnClick={handleShowHiden}
              type="danger"
            />
          </div>
        </div>
      )}

      {comics.length > 0 || loading ? (
        <CharactersLayout>
          {loading
            ? [...Array(limit).keys()].map((loadingComic) => (
                <ComicPreview key={loadingComic} skeleton />
              ))
            : comics.map((comic) => {
                const comicIsHiden =
                  hidenComics.find(
                    (hidenComic) => hidenComic.id === comic.id
                  ) !== undefined;
                if (comicIsHiden) {
                  return null;
                }
                const comicIsBookmarked =
                  bookmarkedComics.find(
                    (bookmarkedComic) => bookmarkedComic.id === comic.id
                  ) !== undefined;
                const handleComicClick = () =>
                  navigate(`${RoutesNames.COMICS}/${comic.id}`);
                const handleAddToBookmark = () => {
                  if (comic.id) {
                    dispatch({
                      type: BookMarksActionType.BOOKMARKS_ADD_BOOKMARK,
                      payload: { type: 'comic', id: comic.id },
                    });
                  }
                };
                const handleRemoveFromBookmark = () => {
                  if (comic.id) {
                    dispatch({
                      type: BookMarksActionType.BOOKMARKS_REMOVE_BOOKMARK,
                      payload: { type: 'comic', id: comic.id },
                    });
                  }
                };
                const handleAddToHidenItems = () => {
                  if (comic.id) {
                    dispatch({
                      type: HidenItemsActionType.HIDEN_ITEMS_ADD_HIDEN_ITEM,
                      payload: { type: 'comic', id: comic.id },
                    });
                  }
                };
                const options: IContextMenuOption[] = [
                  {
                    text: 'Hide',
                    onClick: handleAddToHidenItems,
                  },
                ];
                if (comicIsBookmarked) {
                  options.push({
                    text: 'Remove from bookmarks',
                    onClick: handleRemoveFromBookmark,
                  });
                } else {
                  options.push({
                    text: 'Add to bookmarks',
                    onClick: handleAddToBookmark,
                  });
                }

                const yearString = comic.dates?.find(
                  (date) => date.type === 'onsaleDate'
                )?.date;
                const year = yearString
                  ? new Date(yearString).getFullYear()
                  : new Date().getFullYear();
                return (
                  <ContextMenuLayer key={comic.id} options={options}>
                    <ComicPreview
                      bookmarked={comicIsBookmarked}
                      handleOnClick={handleComicClick}
                      image={`${comic.thumbnail?.path}/portrait_xlarge.${comic.thumbnail?.extension}`}
                      title={comic.title ? comic.title : ''}
                      year={year}
                    />
                  </ContextMenuLayer>
                );
              })}
        </CharactersLayout>
      ) : (
        <NoDataFound />
      )}

      <div className="comics--pagination--container">
        <Pagination
          page={page}
          pages={totalPages}
          handlePageClick={handlePageClick}
        />
      </div>
    </div>
  );
};

export default Comics;
